import { dict, type MsgKey } from "@/i18n/strings";
import { useLocaleStore } from "@/store/locale";

/** 一句固定报错的三种原文，对应同一个界面文案 */
interface ViralErrorRow {
  /** 界面文案 key */
  key: MsgKey;
  /** 后端可能返回的英文、中文、印尼语原文 */
  texts: readonly string[];
}

/**
 * 爆款库固定报错。带数量、时间或第三方明细的句子不在这里，仍按服务端语言展示。
 * 错误码表里的英文「Data sync is in progress...」和状态文案「Data is updating...」是同一件事。
 */
const VIRAL_ERROR_ROWS: readonly ViralErrorRow[] = [
  {
    key: "inspErrVideoNotReady",
    texts: [
      "The video is not ready for preview yet. Please try again later.",
      "视频暂不可预览，请稍后重试",
      "Video belum bisa dipratinjau. Coba lagi nanti.",
    ],
  },
  {
    key: "inspErrDataUpdating",
    texts: [
      "Data is updating. Please refresh later.",
      "Data sync is in progress. Please refresh later.",
      "数据更新中，请稍后刷新",
      "Data sedang diperbarui. Segarkan nanti.",
    ],
  },
  {
    key: "inspErrSyncOk",
    texts: ["Sync succeeded", "同步成功", "Sinkron berhasil"],
  },
  {
    key: "inspErrUpdateStale",
    texts: [
      "The update failed. Shown data may be out of date.",
      "数据更新异常，当前展示数据可能不是最新",
      "Pembaruan gagal. Data yang ditampilkan mungkin belum terbaru.",
    ],
  },
  {
    key: "inspErrDownloadMissing",
    texts: ["Missing video download URL", "缺少视频下载链接（item_url）", "URL unduhan video tidak ada"],
  },
  {
    key: "inspErrDownloadFailed",
    texts: ["Download failed", "下载失败", "Unduhan gagal"],
  },
  {
    key: "inspErrSyncRunning",
    texts: ["A sync job is already running", "已有同步任务运行中", "Tugas sinkron sudah berjalan"],
  },
  {
    key: "inspErrSyncSkipped",
    texts: ["Sync is already running", "同步任务已在运行", "Sinkronisasi sudah berjalan"],
  },
  {
    key: "inspErrSyncRetry",
    texts: ["Sync failed. Please try again later.", "同步失败，请稍后重试", "Sinkron gagal. Coba lagi nanti."],
  },
  {
    key: "inspErrNoSync",
    texts: ["No sync records yet", "暂无同步记录", "Belum ada catatan sinkron"],
  },
  {
    key: "inspErrTikHubToken",
    texts: [
      "TikHub API token is missing or invalid. Check tikhub.api-token.",
      "TikHub API Token 无效或未配置，请检查 tikhub.api-token",
      "Token API TikHub tidak ada atau tidak valid. Periksa tikhub.api-token.",
    ],
  },
  {
    key: "inspErrYoutubeNoMux",
    texts: [
      "YouTube source has no mp4 video and audio to combine",
      "YouTube 播放源没有可封装的 mp4 音视频",
      "Sumber YouTube tidak punya video dan audio mp4 untuk digabung",
    ],
  },
  {
    key: "inspErrYoutubeDownload",
    texts: ["YouTube download failed", "YouTube 成片下载失败", "Unduhan YouTube gagal"],
  },
  {
    key: "inspErrYoutubeId",
    texts: ["Invalid YouTube video id", "YouTube videoId 无效", "ID video YouTube tidak valid"],
  },
  {
    key: "inspErrYoutubeNoSource",
    texts: ["No YouTube playback source is available", "没有可用的 YouTube 播放源", "Tidak ada sumber pemutaran YouTube"],
  },
  {
    key: "inspErrPlaybackEmpty",
    texts: ["Playback source returned an empty response", "播放源响应为空", "Sumber pemutaran mengembalikan respons kosong"],
  },
  {
    key: "inspErrMediaSmall",
    texts: ["Media file is too small", "媒体文件过小", "File media terlalu kecil"],
  },
];

const VIRAL_ERROR_KEYS = new Map<string, MsgKey>(VIRAL_ERROR_ROWS.flatMap((row) => row.texts.map((text) => [text, row.key] as const)));

/** 已知爆款库报错换成当前界面语言；不认识的原文返回 null */
export function localizeViralErrorMessage(message: string): string | null {
  const key = VIRAL_ERROR_KEYS.get(message.trim());
  if (!key) {
    return null;
  }
  return dict[key][useLocaleStore().locale];
}
